import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, DollarSign, ClipboardList, ReceiptText, BellRing, CheckCircle2, Circle, Bell, BellOff, Send, Sparkles, Building2, Globe, ExternalLink, ChevronRight, Inbox } from "lucide-react";
import { pushSupported, getCurrentPushPermission, getCurrentSubscription, enableAdminPush, disableAdminPush, sendTestPush } from "@/lib/push";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import FirstLoginChecklist from "@/components/FirstLoginChecklist";
import StudioOnboardingWizard from "@/components/StudioOnboardingWizard";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [studioBanner, setStudioBanner] = useState(null); // {studio, total_studios}
  const [siteSettings, setSiteSettings] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  // First-run detection: open the onboarding wizard when this is a fresh
  // (non-Allure) tenant whose key public-site fields are still empty AND
  // they haven't completed/dismissed the wizard yet. The Allure founder
  // studio is exempt — its data is backfilled by the server-side scrub.
  useEffect(() => {
    api.get("/admin/site").then((r) => {
      setSiteSettings(r.data);
      const s = r.data || {};
      const isAllure = s.studio_id === "allure-image";
      const alreadyDone = !!s.onboarding_wizard_completed_at;
      const blank = !s.hero_eyebrow && !s.contact_email;
      if (!isAllure && !alreadyDone && blank) {
        setWizardOpen(true);
      }
    }).catch(() => { /* silent — wizard simply won't open */ });
  }, []);

  const loadReminders = useCallback(async () => {
    try { const { data } = await api.get("/admin/reminders/status"); setReminders(data); }
    catch (err) { console.warn("reminders load failed", err); setReminders(null); }
  }, []);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    loadReminders();
    // Pull the multi-tenancy status so the dashboard can show a small "Lumetier beta"
    // banner with the current studio identity + roster size. This is what makes the new
    // multi-tenant world visible to the admin without changing the rest of the UI.
    api.get("/admin/migration/multitenant-status")
      .then((r) => setStudioBanner({ studio: r.data.current_studio, total: r.data.total_studios_in_db }))
      .catch((err) => console.warn("multitenant status load failed", err));
  }, [loadReminders]);

  async function runRemindersNow() {
    try {
      const { data } = await api.post("/admin/reminders/run-now");
      toast.success(`Reminder check ran — sent ${data.sent} email${data.sent === 1 ? "" : "s"}.`);
      loadReminders();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  const cards = [
    {
      label: "Upcoming sessions", value: stats?.upcoming_bookings ?? "—",
      icon: CalendarDays, testid: "stat-upcoming",
      sub: "scheduled & confirmed",
    },
    {
      label: "Pending bookings", value: stats?.pending_bookings ?? "—",
      icon: ClipboardList, testid: "stat-pending",
      sub: "awaiting confirmation",
    },
    {
      label: "Revenue (paid)", value: stats ? `$${(stats.revenue || 0).toFixed(0)}` : "—",
      icon: DollarSign, testid: "stat-revenue",
      sub: "from settled invoices",
    },
    {
      label: "Open invoices", value: stats?.pending_invoices ?? "—",
      icon: ReceiptText, testid: "stat-invoices",
      sub: "sent · unpaid",
    },
  ];

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const firstName = user?.email?.split("@")[0] || "";

  // Pre-compute "all caught up" state
  const allCaughtUp = stats && (stats.pending_bookings ?? 0) === 0 && (stats.pending_invoices ?? 0) === 0;
  const today = new Date();

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      {wizardOpen && (
        <StudioOnboardingWizard
          initial={siteSettings}
          onClose={(result) => {
            setWizardOpen(false);
            // Re-fetch settings so the rest of the dashboard reflects newly
            // saved studio name / hero eyebrow if the user finished the wizard.
            if (result?.completed) {
              api.get("/admin/site").then((r) => setSiteSettings(r.data)).catch(() => {});
            }
          }}
        />
      )}

      {/* ── Hero greeting band ──────────────────────────────────────────────── */}
      <section className="relative pb-2">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))] font-medium mb-3">
              {today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[0.95]">
              {greeting}
              {firstName ? (
                <>
                  ,{" "}
                  <span className="italic text-[hsl(var(--gold))]">{firstName}</span>
                </>
              ) : ""}
              <span className="text-muted-foreground/60"> ·</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-4 max-w-xl">
              {allCaughtUp
                ? "Studio's quiet — no pending bookings or invoices on the wire."
                : "Here's what's moving across the studio right now."}
            </p>
          </div>
        </div>
      </section>

      <FirstLoginChecklist studioId={studioBanner?.studio?.id || user?.studio_id} />

      {/* ── 'You're all caught up' card — sparkly gold lockup ───────────────── */}
      {allCaughtUp && (
        <div
          className="relative overflow-hidden border border-[hsl(var(--gold))]/25 bg-card flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6 p-6 sm:p-7 group hover:border-[hsl(var(--gold))]/45 transition-colors"
          data-testid="all-caught-up-card"
        >
          <div className="absolute -top-12 -right-12 w-48 h-48 gold-aura rounded-full pointer-events-none" />
          <div className="relative w-14 h-14 rounded-full bg-[hsl(var(--gold))]/12 border border-[hsl(var(--gold))]/35 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-[hsl(var(--gold))]" strokeWidth={1.5} />
          </div>
          <div className="relative flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))] font-medium">
              All caught up
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-light mt-1.5 leading-tight">
              Inbox zero, beautifully.
            </h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-md">
              Every booking is confirmed and every invoice is settled. Treat yourself to a slow espresso.
            </p>
          </div>
          <Link
            to="/admin/galleries"
            className="relative shrink-0 inline-flex items-center gap-1.5 px-4 h-10 border border-border hover:border-[hsl(var(--gold))]/50 text-[11px] uppercase tracking-[0.22em] transition-colors"
            data-testid="all-caught-up-cta"
          >
            Curate galleries <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      )}

      {studioBanner?.studio && (
        <div className="space-y-3">
          {(() => {
            // Founder-only banner sub-content: only the platform owner sees the
            // total studio count + "Invite another photographer" CTA. Tenants
            // get a calmer banner showing just their own studio details (and
            // the whole row stops being a link to /admin/studio-invites,
            // which would 403-redirect them anyway).
            const isPlatformOwner = user?.studio_id === "allure-image";
            const BannerWrap = isPlatformOwner ? Link : "div";
            const wrapProps = isPlatformOwner
              ? { to: "/admin/studio-invites", className: "block border border-border bg-card px-5 py-4 hover:border-[hsl(var(--gold))]/50 transition-colors group" }
              : { className: "block border border-border bg-card px-5 py-4" };
            return (
          <BannerWrap
            {...wrapProps}
            data-testid="multitenancy-banner"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))]/30 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--gold))]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--gold))] font-medium">
                    {isPlatformOwner ? "Lumetier multi-tenant · live" : "Powered by Lumetier"}
                  </div>
                  <div className="text-sm mt-0.5">
                    Studio: <strong>{studioBanner.studio.name}</strong>
                    <span className="text-muted-foreground"> · </span>
                    <span className="font-mono text-xs">{studioBanner.studio.slug}</span>
                    <span className="text-muted-foreground"> · </span>
                    <span className="text-xs uppercase tracking-wide">{studioBanner.studio.plan}</span>
                  </div>
                </div>
              </div>
              {isPlatformOwner && (
                <div className="text-right text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                  <div className="inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" />
                    {studioBanner.total} {studioBanner.total === 1 ? "studio" : "studios"} on Lumetier
                  </div>
                  <div className="mt-0.5 flex items-center justify-end gap-1">
                    Invite another photographer
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              )}
            </div>
          </BannerWrap>
            );
          })()}

          {/* Public booking URL surface relocated to Site Settings → Branding
              → Live Links panel (data-testid="live-links-panel") per user
              request. Overview stays focused on metrics + the studio banner;
              the link lives where the rest of the site config lives. */}
        </div>
      )}

      {/* ── Stat tiles — hairline cream-on-charcoal editorial tiles ─────────── */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-b border-[color:var(--gold-foil)]/30 divide-y sm:divide-y-0 sm:divide-x divide-[color:var(--gold-foil)]/20" data-testid="stat-tiles">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className="bg-[color:var(--charcoal)] text-[color:var(--cream)] p-7 flex flex-col justify-between min-h-[180px] group hover:bg-[color:var(--charcoal)]/90 transition-colors"
              data-testid={c.testid}
            >
              <div className="flex items-start justify-between">
                <span className="ed-eyebrow text-[color:var(--gold-base)]">{c.label}</span>
                <Icon className="w-4 h-4 text-[color:var(--gold-base)]/70" strokeWidth={1.25} />
              </div>
              <div>
                <div className="font-serif text-5xl font-light italic tabular-nums leading-none mt-6">{c.value}</div>
                <div className="text-[11px] text-[color:var(--cream)]/55 mt-3 tracking-wide">{c.sub}</div>
              </div>
            </div>
          );
        })}
      </div>

      <LeadsCard />

      <AlertRoutingPanel />

      <PushNotificationsPanel />

      {reminders && (
        <div className="bg-card border border-border" data-testid="reminder-panel">
          <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <BellRing className="w-5 h-5 text-muted-foreground" />
              <div>
                <h2 className="font-serif text-2xl">Auto session reminders</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {reminders.running ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Scheduler running</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-rose-400"><Circle className="w-3.5 h-3.5" /> Scheduler offline</span>
                  )}
                  {" · "}Sends {reminders.lead_hours}h before session, checks every {reminders.check_minutes} min ({reminders.studio_tz})
                </p>
              </div>
            </div>
            <button onClick={runRemindersNow} className="inline-flex items-center gap-2 px-4 h-9 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em]" data-testid="run-reminders-now-btn">
              Run check now
            </button>
          </div>

          {reminders.pending_reminders.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No upcoming sessions awaiting a reminder.</div>
          ) : (
            <>
              {/* Mobile: stacked cards */}
              <ul className="lg:hidden divide-y divide-border">
                {reminders.pending_reminders.slice(0, 6).map((r) => (
                  <li key={r.reference} className="p-4 space-y-1.5" data-testid={`pending-reminder-${r.reference}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.customer_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.customer_email}</div>
                      </div>
                      {r.in_window ? (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">Queued</span>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-border text-muted-foreground">Waiting</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{r.session_date} · {r.session_time}</div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-muted-foreground">{r.reference}</span>
                      <span className="tabular-nums">{r.hours_until.toFixed(1)} h</span>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop: full table */}
              <table className="hidden lg:table w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3 eyebrow font-normal">Ref</th>
                    <th className="px-6 py-3 eyebrow font-normal">Client</th>
                    <th className="px-6 py-3 eyebrow font-normal">Session</th>
                    <th className="px-6 py-3 eyebrow font-normal">In</th>
                    <th className="px-6 py-3 eyebrow font-normal text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reminders.pending_reminders.slice(0, 6).map((r) => (
                    <tr key={r.reference} className="border-t border-border" data-testid={`pending-reminder-desktop-${r.reference}`}>
                      <td className="px-6 py-3 font-mono text-xs">{r.reference}</td>
                      <td className="px-6 py-3">{r.customer_name}<div className="text-xs text-muted-foreground">{r.customer_email}</div></td>
                      <td className="px-6 py-3">{r.session_date} {r.session_time}</td>
                      <td className="px-6 py-3 tabular-nums">{r.hours_until.toFixed(1)} h</td>
                      <td className="px-6 py-3 text-right">
                        {r.in_window ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">Queued — next tick</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border border-border text-muted-foreground">Waiting</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <div className="bg-card border border-border" data-testid="recent-bookings-panel">
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Activity</div>
            <h2 className="font-serif text-2xl font-light mt-1">Recent bookings</h2>
          </div>
          <Link
            to="/admin/bookings"
            className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-[hsl(var(--gold))] transition-colors inline-flex items-center gap-1.5"
            data-testid="dashboard-view-bookings"
          >
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Mobile: stacked cards */}
        <ul className="lg:hidden divide-y divide-border">
          {(stats?.recent_bookings || []).map((b) => (
            <li key={b.id} className="p-4 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{b.service_name} · {b.tier_name}</div>
                </div>
                <StatusBadge value={b.status} />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{b.session_date} · {b.session_time}</span>
                <span className="font-mono text-muted-foreground">{b.reference}</span>
              </div>
            </li>
          ))}
          {(!stats?.recent_bookings || stats.recent_bookings.length === 0) && (
            <li className="px-4 py-10 text-center text-muted-foreground text-sm">No bookings yet.</li>
          )}
        </ul>

        {/* Desktop: full table */}
        <table className="hidden lg:table w-full text-sm">
          <thead>
            <tr>
              <th className="px-6 py-3.5 text-left text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Ref</th>
              <th className="px-6 py-3.5 text-left text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Client</th>
              <th className="px-6 py-3.5 text-left text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Session</th>
              <th className="px-6 py-3.5 text-left text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Date</th>
              <th className="px-6 py-3.5 text-left text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_bookings || []).map((b) => (
              <tr key={b.id} className="border-t border-border/60 hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{b.reference}</td>
                <td className="px-6 py-4">{b.customer_name}</td>
                <td className="px-6 py-4 text-muted-foreground">{b.service_name} · {b.tier_name}</td>
                <td className="px-6 py-4 text-muted-foreground tabular-nums">{b.session_date} {b.session_time}</td>
                <td className="px-6 py-4"><StatusBadge value={b.status} /></td>
              </tr>
            ))}
            {(!stats?.recent_bookings || stats.recent_bookings.length === 0) && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground text-sm">No bookings yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PushNotificationsPanel() {
  const [supported] = useState(() => pushSupported());
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [serverDevices, setServerDevices] = useState(null); // { count, devices: [{host,created_at}] }
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    // Always pull what the server thinks — this is the source of truth for whether
    // pushes can actually be delivered. The browser-side subscription only tells us
    // whether iOS *could* receive one.
    try {
      const { data } = await api.get("/admin/push/subscriptions");
      setServerDevices(data);
    } catch (err) { console.warn("push subscriptions load failed", err); setServerDevices(null); }
    if (!supported) return;
    setPermission(await getCurrentPushPermission());
    const sub = await getCurrentSubscription();
    setSubscribed(!!sub);
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);

  // iOS Safari only exposes Push API when the site is launched from the Home Screen.
  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );

  async function onEnable() {
    setBusy(true);
    try {
      await enableAdminPush();
      toast.success("Push notifications enabled on this device.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not enable push notifications.");
    } finally { setBusy(false); }
  }

  async function onDisable() {
    setBusy(true);
    try {
      await disableAdminPush();
      toast.success("Push notifications disabled on this device.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not disable push notifications.");
    } finally { setBusy(false); }
  }

  async function onTest() {
    setTesting(true);
    try {
      let result = await sendTestPush();
      // Self-heal: if the browser thinks it's subscribed but the server has no record
      // (most common cause: an earlier subscribe POST 404'd before the routing fix),
      // re-sync the subscription server-side and try the test exactly once more.
      let healAttempted = false;
      let healError = null;
      if (result && result.total === 0 && subscribed) {
        healAttempted = true;
        try {
          await enableAdminPush();
          await refresh();
          result = await sendTestPush();
        } catch (e) {
          healError = e?.response?.data?.detail || e?.message || "auto-resync failed";
        }
      }
      const { sent, total, errors, message } = result || {};
      // Prefer surfacing the real server-side error over a generic count
      if (errors && errors.length > 0 && sent === 0) {
        const first = errors[0];
        toast.error(`Push failed — ${first.host || "server"} returned ${first.status || "error"}${first.detail ? `: ${first.detail.slice(0, 80)}` : ""}`, { duration: 10000 });
      } else if (sent > 0) {
        toast.success(`Test push delivered (${sent}/${total}). If you don't see it, check Settings → Notifications → Allure Image.`);
      } else if (total === 0) {
        const diag = result?.queried_user_email
          ? ` [queried as "${result.queried_user_email}", ${result.total_in_db_any_user ?? "?"} sub(s) in DB total]`
          : "";
        toast.error((message || "No devices subscribed yet on this account.") + diag, { duration: 12000 });
        if (healAttempted && healError) toast.error(`Auto re-sync also failed: ${healError}`, { duration: 10000 });
      } else {
        toast.error(`Push failed (0/${total} delivered). Disable + re-enable to refresh the subscription.`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send test push.");
    } finally { setTesting(false); }
  }

  async function onResync() {
    setBusy(true);
    try {
      await enableAdminPush();
      toast.success("Device re-synced. Tap 'Send test' again.");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not re-sync this device.");
    } finally { setBusy(false); }
  }

  async function onClearAll() {
    if (!window.confirm("Remove every push subscription from the server for this account? You'll then need to tap Enable on each of your devices again.")) return;
    setBusy(true);
    try {
      // Also unsubscribe the local browser so we start truly clean
      try { await disableAdminPush(); } catch (_) { /* ok if nothing to unsubscribe */ }
      const { data } = await api.post("/admin/push/clear");
      toast.success(`Cleared ${data.removed} stale device(s). Tap 'Enable on this device' to re-register.`);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not clear subscriptions.");
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-card border border-border" data-testid="push-panel">
      <div className="p-6 border-b border-border flex items-start gap-3">
        {subscribed ? <Bell className="w-5 h-5 text-muted-foreground mt-1" /> : <BellOff className="w-5 h-5 text-muted-foreground mt-1" />}
        <div className="min-w-0">
          <h2 className="font-serif text-2xl">Push notifications</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Get an instant alert on this device when a new booking comes in — even when the app is closed.
          </p>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {!supported && (
          <p className="text-sm text-muted-foreground" data-testid="push-unsupported">
            This browser doesn&apos;t support push notifications. On iPhone, tap <span className="font-medium">Share → Add to Home Screen</span>, then open the installed app to enable.
          </p>
        )}

        {supported && !isStandalone && /iPad|iPhone|iPod/.test(navigator.userAgent) && (
          <p className="text-sm text-muted-foreground" data-testid="push-ios-hint">
            On iPhone you must install the app first: tap <span className="font-medium">Share → Add to Home Screen</span>, then open it from your home screen to enable notifications.
          </p>
        )}

        {supported && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground" data-testid="push-server-status">
              Server has{" "}
              <strong className="text-foreground">{serverDevices?.count ?? "…"}</strong>{" "}
              device{serverDevices?.count === 1 ? "" : "s"} subscribed for this account.
              {serverDevices?.devices?.length > 0 && (
                <span className="block mt-0.5 text-[11px] text-muted-foreground/80">
                  {serverDevices.devices.map((d) => d.host).join(" · ")}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
            {!subscribed ? (
              <button
                onClick={onEnable}
                disabled={busy || permission === "denied"}
                data-testid="push-enable-btn"
                className="inline-flex items-center gap-2 px-4 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90 disabled:opacity-60"
              >
                <Bell className="w-3.5 h-3.5" />
                {busy ? "Enabling…" : "Enable on this device"}
              </button>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] border border-emerald-500/40 text-emerald-300 bg-emerald-500/10" data-testid="push-status-enabled">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Enabled
                </span>
                <button
                  onClick={onTest}
                  disabled={testing}
                  data-testid="push-test-btn"
                  className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
                >
                  <Send className="w-3.5 h-3.5" />
                  {testing ? "Sending…" : "Send test"}
                </button>
                <button
                  onClick={onResync}
                  disabled={busy}
                  data-testid="push-resync-btn"
                  title="Re-register this device with the server (use if test pushes aren't arriving)"
                  className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-60"
                >
                  <Bell className="w-3.5 h-3.5" />
                  {busy ? "Syncing…" : "Re-sync"}
                </button>
                <button
                  onClick={onDisable}
                  disabled={busy}
                  data-testid="push-disable-btn"
                  className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground disabled:opacity-60"
                >
                  <BellOff className="w-3.5 h-3.5" />
                  {busy ? "Disabling…" : "Disable"}
                </button>
              </>
            )}
            {(serverDevices?.count ?? 0) > 0 && (
              <button
                onClick={onClearAll}
                disabled={busy}
                data-testid="push-clear-btn"
                title="Remove every saved subscription on this account — use as a last-resort reset"
                className="inline-flex items-center gap-2 px-4 h-10 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
              >
                <BellOff className="w-3.5 h-3.5" />
                Clear all
              </button>
            )}
            {permission === "denied" && (
              <span className="text-xs text-rose-400" data-testid="push-denied-msg">
                Notifications were blocked. Enable them for this site in your browser/device settings.
              </span>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function StatusBadge({ value }) {
  const styles = {
    pending: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    confirmed: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    completed: "border-sky-500/40 text-sky-300 bg-sky-500/10",
    cancelled: "border-rose-500/40 text-rose-300 bg-rose-500/10",
    draft: "border-stone-500/40 text-stone-300 bg-stone-500/10",
    sent: "border-sky-500/40 text-sky-300 bg-sky-500/10",
    paid: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    overdue: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${styles[value] || ""}`}>
      {value}
    </span>
  );
}


/** Overview leads card — new inquiries, stale ones, and this month's conversion. */
function LeadsCard() {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    api.get("/admin/inquiries/counts").then((r) => setLeads(r.data)).catch(() => setLeads({}));
  }, []);
  const month = new Date().toLocaleString(undefined, { month: "long" });
  const cells = [
    { label: "New inquiries", value: leads?.new ?? "—", sub: "unanswered", testid: "leads-new", hot: (leads?.new ?? 0) > 0 },
    { label: "Waiting 24h+", value: leads?.awaiting_reply_24h ?? "—", sub: "going cold", testid: "leads-stale", hot: (leads?.awaiting_reply_24h ?? 0) > 0 },
    { label: "Client replies", value: leads?.client_replied ?? "—", sub: "awaiting you", testid: "leads-replied", hot: (leads?.client_replied ?? 0) > 0 },
    { label: `${month} conversion`, value: leads ? `${leads.conversion_rate ?? 0}%` : "—", sub: leads ? `${leads.month_booked ?? 0} booked of ${leads.month_received ?? 0}` : "", testid: "leads-conversion" },
  ];
  return (
    <div className="bg-card border border-border" data-testid="leads-card">
      <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Inbox className="w-5 h-5 text-muted-foreground" />
          <div>
            <h2 className="font-serif text-2xl">Leads</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Inquiries from your website's contact form — reply fast, convert more.</p>
          </div>
        </div>
        <Link to="/admin/inquiries" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground transition-colors" data-testid="leads-open-inbox">
          Open inbox <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-border">
        {cells.map((c) => (
          <Link to="/admin/inquiries" key={c.label} className="p-6 hover:bg-muted/40 transition-colors" data-testid={c.testid}>
            <div className="ed-eyebrow text-muted-foreground">{c.label}</div>
            <div className={`font-serif text-4xl font-light italic tabular-nums mt-4 ${c.hot ? "text-rose-500" : ""}`}>{c.value}</div>
            <div className="text-[11px] text-muted-foreground mt-2 tracking-wide">{c.sub}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}


/** Where this studio's alerts go + one-click real test (email + push). */
function AlertRoutingPanel() {
  const [routing, setRouting] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.get("/admin/notifications/routing").then((r) => setRouting(r.data)).catch(() => setRouting({}));
  }, []);
  async function sendTest() {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/notifications/test");
      setResult(data);
      const pushSent = data.push?.sent ?? 0;
      if (data.email_sent || pushSent > 0) {
        toast.success(`Test sent — email ${data.email_sent ? "✓" : "✗"} · push ${pushSent}/${data.push?.total ?? 0} device${(data.push?.total ?? 0) === 1 ? "" : "s"}`);
      } else {
        toast.error(data.email_error || data.push?.message || "Nothing could be delivered — check the details below.", { duration: 10000 });
      }
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setTesting(false); }
  }
  return (
    <div className="bg-card border border-border" data-testid="alert-routing-panel">
      <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <BellRing className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h2 className="font-serif text-2xl">Where your alerts go</h2>
            <p className="text-xs text-muted-foreground mt-1 break-words" data-testid="alert-routing-email">
              Email alerts → <strong className="text-foreground">{routing ? (routing.alert_email || "no inbox configured") : "…"}</strong>
              {routing?.push_devices != null && (
                <> · Push → <strong className="text-foreground">{routing.push_devices}</strong> device{routing.push_devices === 1 ? "" : "s"}</>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground/80 mt-1">
              Change the inbox in <Link to="/admin/site?tab=booking" className="underline">Site Settings → Booking → Notification email</Link>. Bookings, inquiries, gallery selections and reviews all use this route.
            </p>
            {result && (
              <p className="text-[11px] mt-2" data-testid="alert-test-result">
                <span className={result.email_sent ? "text-emerald-500" : "text-rose-500"}>Email {result.email_sent ? "delivered to " + result.alert_email : "failed" + (result.email_error ? ` — ${result.email_error}` : "")}</span>
                {" · "}
                <span className={(result.push?.sent ?? 0) > 0 ? "text-emerald-500" : "text-rose-500"}>
                  Push {result.push?.sent ?? 0}/{result.push?.total ?? 0}{result.push?.errors?.length ? ` — ${result.push.errors[0]?.error || result.push.errors[0]}` : ""}
                </span>
              </p>
            )}
          </div>
        </div>
        <button
          onClick={sendTest}
          disabled={testing}
          className="inline-flex items-center gap-2 px-4 h-10 border border-border text-xs uppercase tracking-[0.18em] hover:bg-muted disabled:opacity-60 shrink-0"
          data-testid="alert-test-btn"
        >
          <Send className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send test alert"}
        </button>
      </div>
    </div>
  );
}
